/**
 * API client for MTGJSON endpoints
 */

import { api, ensureApiAuthenticated } from '../services/api';

export interface MTGJSONMetadata {
  version: string | null;
  date: string | null;
}

/**
 * Fetch MTGJSON metadata (version, build date, last sync)
 */
export async function fetchMTGJSONMetadata(): Promise<MTGJSONMetadata> {
  await ensureApiAuthenticated();
  const response = await api.get('/mtgjson/metadata');
  return response.data;
}

export interface SyncServiceStatus {
  last_run_at: string | null;
  records_processed: number;
  errors: number;
  failure_rate: number;
  status: 'success' | 'warning' | 'failed';
  details: Record<string, unknown>;
}

export interface SyncStatus {
  services: Record<string, SyncServiceStatus>;
  overall_health: 'healthy' | 'warning' | 'error';
  last_successful_sync: string | null;
}

/**
 * Fetch comprehensive sync status for all services
 */
export async function fetchSyncStatus(): Promise<SyncStatus> {
  await ensureApiAuthenticated();
  const response = await api.get('/mtgjson/sync-status');
  return response.data;
}

/**
 * Manually trigger MTGJSON sync
 */
export async function triggerMTGJSONSync(): Promise<Record<string, unknown>> {
  await ensureApiAuthenticated();
  const response = await api.post('/mtgjson');
  return response.data as Record<string, unknown>;
}

export interface LoadStepStatus {
  status: 'success' | 'warning' | 'failed';
  records_processed: number;
  total_records: number;
  progress_percent: number;
  phase_progress_percent?: number;
  overall_progress_percent?: number;
  started_at: string | null;
  errors: number;
  phase: string;
  metadata_stage?: string | null;
  stage?: string | null;
  current_activity?: string | null;
  activity_updated_at?: string | null;
  last_log_line?: string | null;
  timed_out_at?: string | null;
  timed_out_after_seconds?: number | null;
}

export interface ContinuitySnapshotAppMetrics {
  products_total: number;
  products_with_mtgjson_uuid: number;
  products_with_tcg_market_price: number;
  products_with_preferred_price: number;
  pricing_snapshots_last_24h: number;
  mtgjson_uuid_coverage_pct: number;
  tcg_market_coverage_pct: number;
  preferred_price_coverage_pct: number;
}

export interface ContinuitySnapshotMtgjsonMetrics {
  db_available: boolean;
  error: string | null;
  cards_rows: number | null;
  allidentifiers_rows: number | null;
  cardprices_rows: number | null;
}

export interface ContinuitySnapshot {
  captured_at: string;
  app: ContinuitySnapshotAppMetrics;
  mtgjson: ContinuitySnapshotMtgjsonMetrics;
}

export interface TcgcsvCatalogStatus {
  database: 'tcgcsv';
  service_name: 'tcgcsv_catalog';
  status: string;
  last_run_at: string | null;
  last_success_at: string | null;
  category_ids: number[];
  group_count: number | null;
  product_count: number | null;
  price_count: number | null;
  price_snapshot_at: string | null;
  records_processed: number;
  errors: number;
  db_available: boolean;
  error: string | null;
}

export interface LoadStatus {
  current_run_id: string | null;
  overall_status: 'not_started' | 'loading' | 'completed' | 'failed';
  current_step: string | null;
  steps: Record<string, LoadStepStatus>;
  total_progress: number;
  started_at: string | null;
  ended_at: string | null;
  elapsed_seconds: number;
  estimated_completion: number | null;
  current_step_phase_progress?: number | null;
  current_step_overall_progress?: number | null;
  continuity_snapshot?: ContinuitySnapshot | null;
  current_activity?: string | null;
  activity_updated_at?: string | null;
  last_log_line?: string | null;
  run_errors?: string[];
  /** Pokémon + Lorcana catalog in the dedicated `tcgcsv` database — not MTGJSON UUID coverage. */
  tcgcsv?: TcgcsvCatalogStatus | null;
}

/**
 * Fetch MTGJSON background load status
 */
export async function fetchLoadStatus(): Promise<LoadStatus> {
  await ensureApiAuthenticated();
  const response = await api.get('/mtgjson/load-status');
  return response.data;
}
